import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Cog, Save, Loader2, Plus, X, GripVertical } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GearboxTypesEditor — admin-editable list of gearbox/transmission
 * variants that show up in the New Order wizard step 2 (Gearbox type).
 * Mirrors the Reading Methods editor in shape and UX so the admin
 * doesn't have to learn two different patterns.
 */
export default function GearboxTypesEditor({ token }) {
  const [types, setTypes]     = useState([]);
  const [csv, setCsv]         = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/config/gearbox-types`);
      if (!r.ok) return;
      const data = await r.json();
      const list = Array.isArray(data) ? data : [];
      setTypes(list);
      setCsv(list.join(', '));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCsv = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/config/gearbox-types`, {
        method: 'PUT', headers, body: JSON.stringify({ types_csv: csv }),
      });
      if (!r.ok) { alert('Save failed: ' + await r.text()); return; }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      load();
    } finally { setSaving(false); }
  };

  const removeOne = async (m) => {
    const next = types.filter((x) => x !== m);
    setTypes(next);
    setCsv(next.join(', '));
    await fetch(`${API_URL}/api/config/gearbox-types`, {
      method: 'PUT', headers, body: JSON.stringify({ types: next }),
    });
  };

  const addOne = async () => {
    const v = window.prompt('Enter a new gearbox type:');
    if (!v) return;
    const next = [...new Set([...types, v.trim()])].filter(Boolean);
    setTypes(next);
    setCsv(next.join(', '));
    await fetch(`${API_URL}/api/config/gearbox-types`, {
      method: 'PUT', headers, body: JSON.stringify({ types: next }),
    });
  };

  // ── Drag-and-drop reorder ──
  // Native HTML5 DnD — no extra deps needed for a flat list. Track the
  // index being dragged in a ref so it survives re-renders during the
  // drag, then splice/insert into a new array on drop. We auto-persist
  // the new order to the backend so the admin doesn't have to remember
  // to click Save after reordering.
  const dragFrom = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const onDragStart = (idx) => (e) => {
    dragFrom.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers need data set on the event for DnD to fire.
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) {}
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOver !== idx) setDragOver(idx);
  };
  const onDragLeave = () => setDragOver(null);
  const onDrop = (idx) => async (e) => {
    e.preventDefault();
    const from = dragFrom.current;
    dragFrom.current = null;
    setDragOver(null);
    if (from === null || from === idx) return;
    const next = [...types];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    setTypes(next);
    setCsv(next.join(', '));
    // Persist immediately — match the auto-save behaviour of add/remove.
    try {
      await fetch(`${API_URL}/api/config/gearbox-types`, {
        method: 'PUT', headers, body: JSON.stringify({ types: next }),
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (_) { /* silent */ }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center text-slate-600">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading gearbox types…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="gearbox-types-editor">
      <div className="flex items-start gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-xl">
        <Cog className="w-5 h-5 text-cyan-700 mt-0.5" />
        <div className="text-sm text-cyan-900">
          <p className="font-semibold mb-1">Gearbox Types</p>
          <p className="text-cyan-800">
            These appear in the New Order wizard, in the <em>Gearbox type</em> dropdown
            on step 2. Edit the comma-separated list below or add/remove items individually.
          </p>
        </div>
      </div>

      {/* CSV editor */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="font-bold text-slate-800 mb-1">Comma-separated list</h3>
        <p className="text-xs text-slate-500 mb-3">
          Tip: separate items with commas. Duplicates are merged automatically.
        </p>
        <textarea
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          rows={6}
          data-testid="gearbox-types-csv"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-cyan-500 outline-none"
        />
        <div className="flex gap-2 mt-3 items-center">
          <button
            onClick={saveCsv}
            disabled={saving}
            data-testid="save-gearbox-types"
            className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium disabled:opacity-50 hover:shadow-md flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save list
          </button>
          {savedFlash && (
            <span className="text-emerald-600 text-sm font-medium" data-testid="gearbox-types-saved">✓ Saved</span>
          )}
        </div>
      </div>

      {/* Chip list — drag to reorder */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-slate-800">Active gearbox types <span className="text-slate-400 font-normal">({types.length})</span></h3>
            <p className="text-xs text-slate-500">Drag the <GripVertical className="inline w-3 h-3 -mt-0.5" /> handle to reorder · click × to remove · order auto-saves.</p>
          </div>
          <button
            onClick={addOne}
            data-testid="add-gearbox-type"
            className="px-4 py-2 bg-cyan-50 text-cyan-700 rounded-lg text-sm font-bold hover:bg-cyan-100 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Add type
          </button>
        </div>
        {types.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No gearbox types configured — clients won't see a dropdown.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="gearbox-types-chips">
            {types.map((m, idx) => (
              <span
                key={m}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDragLeave={onDragLeave}
                onDrop={onDrop(idx)}
                className={`inline-flex items-center gap-1 pl-1.5 pr-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors select-none cursor-grab active:cursor-grabbing ${
                  dragOver === idx
                    ? 'bg-cyan-100 border-cyan-400 ring-2 ring-cyan-300'
                    : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                }`}
                data-testid={`gearbox-type-chip-${m}`}
                title="Drag to reorder"
              >
                <GripVertical className="w-3 h-3 text-slate-400" />
                {m}
                <button onClick={() => removeOne(m)} className="ml-1 text-slate-400 hover:text-rose-600" title="Remove">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
